import React from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

function formatCentral(ts) {
  if (!ts) return '--';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return String(ts);
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(d) + ' CT';
  } catch {
    return String(ts);
  }
}

export default function SettingsSyncPage() {
  const [syncStatus, setSyncStatus] = React.useState([]);
  const [syncLoading, setSyncLoading] = React.useState(true);
  const [syncingEntity, setSyncingEntity] = React.useState(null);
  const [tokenResult, setTokenResult] = React.useState(null);

  const loadSyncStatus = React.useCallback(async () => {
    setSyncLoading(true);
    try {
      const r = await fetch('/api/admin/sync/status', { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setSyncStatus(data?.watermarks || []);
      }
    } catch {}
    setSyncLoading(false);
  }, []);

  React.useEffect(() => { loadSyncStatus(); }, [loadSyncStatus]);

  const getWatermark = React.useCallback((entity) => {
    const list = Array.isArray(syncStatus) ? syncStatus : [];
    return list.find((w) => String(w?.entity || '').toLowerCase() === String(entity || '').toLowerCase()) || null;
  }, [syncStatus]);

  // Check (not refresh) the Meta page tokens + Buffer token. Nothing here can mint a token — page
  // tokens never expire, and the data-access clock resets only when a human re-authorizes at Meta.
  const checkSocialTokens = async () => {
    setSyncingEntity('social_tokens'); setTokenResult(null);
    try {
      const r = await fetch('/api/admin/social-tokens/check', { method: 'POST', credentials: 'include' });
      const data = await r.json();
      setTokenResult(r.ok && data?.success
        ? { healthy: data.healthy, findings: data.findings || [], emailed: data.emailed }
        : { error: data?.error || 'Check failed' });
      await loadSyncStatus(); // refresh "Last checked" from the watermark
    } catch (e) {
      setTokenResult({ error: e?.message || 'Check failed' });
    } finally { setSyncingEntity(null); }
  };

  const runIncremental = async (entity = 'all') => {
    try {
      const res = await fetch(`/api/admin/sync/incremental?entity=${encodeURIComponent(entity)}&schedule=false`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Sync failed (${res.status})`);
      return await res.json();
    } catch (e) {
      console.warn('[Settings][Sync]', e?.message || e);
      return null;
    }
  };

  const doIncremental = async (entity) => {
    setSyncingEntity(entity);
    try {
      await runIncremental(entity);
      await loadSyncStatus();
    } finally {
      setSyncingEntity(null);
    }
  };

  const doBackfill = async (entity) => {
    if (!confirm(`This will sync ALL ${entity} from WooCommerce. This may take a while. Continue?`)) return;
    setSyncingEntity(`backfill-${entity}`);
    try {
      const res = await fetch(`/api/admin/sync/backfill?entity=${encodeURIComponent(entity)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Backfill failed (${res.status})`);
      const data = await res.json();
      await loadSyncStatus();
      const catalog = data?.results?.catalog;
      if (entity === 'catalog' && catalog) {
        const cats = catalog.categories || {};
        const prods = catalog.products || {};
        alert(
          `Catalog backfill completed.\n`
          + `Categories: ${cats.total ?? '?'}\n`
          + `Products: ${prods.total_products ?? '?'}\n`
          + `Variations: ${prods.total_variations ?? '?'}`
        );
      } else {
        alert(`Backfill completed: ${JSON.stringify(data)}`);
      }
    } catch (e) {
      console.error('[Settings] Backfill error:', e?.message || e);
      alert(`Backfill failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setSyncingEntity(null);
    }
  };

  if (syncLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Data &amp; sync
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manual data refresh from WooCommerce and sync status.
      </Typography>
      <Box sx={{ mb: 2, p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
          Action guide
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          Sync Now: Incremental sync for records changed since the last watermark (faster, routine).
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          Backfill All: Full sync for the entire entity from WooCommerce (slower, use for recovery/missing data).
        </Typography>
      </Box>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Manual sync (run now)</Typography>
      <Stack spacing={1.5}>
        {(() => {
          const wm = getWatermark('orders');
          return (
        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>Orders</Typography>
              <Typography variant="caption" color="text.secondary">Syncs orders from WooCommerce (scheduled: every 1 hour)</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Last updated: {formatCentral(wm?.updated_at ?? wm?.last_modified_gmt)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" startIcon={syncingEntity === 'orders' ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={() => doIncremental('orders')} disabled={!!syncingEntity}>Sync Now</Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<RefreshIcon />}
                disabled
              >
                Backfill All
              </Button>
            </Stack>
          </Stack>
        </Box>
          );
        })()}
        {(() => {
          const wm = getWatermark('customers');
          return (
        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>Customers</Typography>
              <Typography variant="caption" color="text.secondary">Syncs customers from WooCommerce (scheduled: every 1 hour)</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Last updated: {formatCentral(wm?.updated_at ?? wm?.last_modified_gmt)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={syncingEntity === 'customers' ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={() => doIncremental('customers')}
                disabled={!!syncingEntity}
              >
                Sync Now
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<RefreshIcon />}
                disabled
              >
                Backfill All
              </Button>
            </Stack>
          </Stack>
        </Box>
          );
        })()}
        {(() => {
          const wm = getWatermark('catalog');
          return (
        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>Catalog (categories + products + variations)</Typography>
              <Typography variant="caption" color="text.secondary">
                Syncs category tree, products, variations, and product→category links (scheduled: every 1 hour)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Last updated: {formatCentral(wm?.updated_at ?? wm?.last_modified_gmt)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" startIcon={syncingEntity === 'catalog' ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={() => doIncremental('catalog')} disabled={!!syncingEntity}>Sync Now</Button>
              <Button size="small" variant="outlined" color="error" startIcon={syncingEntity === 'backfill-catalog' ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={() => doBackfill('catalog')} disabled={!!syncingEntity}>Backfill All</Button>
            </Stack>
          </Stack>
        </Box>
          );
        })()}
        {(() => {
          const wm = getWatermark('social_tokens');
          return (
        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>Social tokens (Meta pages + Buffer)</Typography>
              <Typography variant="caption" color="text.secondary">
                Verifies the Instagram/Facebook page tokens and Buffer are working (checked daily; emails only on a problem).
                Reel posting fails silently without them. This checks — it cannot renew: page tokens never expire, and
                Meta&rsquo;s 90-day data-access clock resets only by re-authorizing in the Graph API Explorer.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Last checked: {formatCentral(wm?.updated_at ?? wm?.last_modified_gmt)}
              </Typography>
              {tokenResult && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}
                  color={tokenResult.error || tokenResult.findings?.some((f) => f.level === 'error') ? 'error.main'
                    : tokenResult.healthy ? 'success.main' : 'warning.main'}>
                  {tokenResult.error ? `Check failed: ${tokenResult.error}`
                    : tokenResult.healthy ? 'All tokens healthy.'
                      : `${tokenResult.findings.length} issue(s)${tokenResult.emailed ? ' — alert emailed' : ''}: `
                        + tokenResult.findings.map((f) => `${f.name}: ${f.message}`).join(' · ')}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined"
                startIcon={syncingEntity === 'social_tokens' ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={checkSocialTokens} disabled={!!syncingEntity}>Check Now</Button>
            </Stack>
          </Stack>
        </Box>
          );
        })()}
      </Stack>
    </>
  );
}
